import { colors, combineStyles, fonts } from '@blend/consumer-components';
import { Field, FormProps, reduxForm } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import * as _ from 'lodash';
import * as React from 'react';

import * as formUtils from 'login-utils/formUtils';
import AddressInput from 'login-components/Common/DataInputs/AddressInput/AddressInput';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import InputMap from 'login-components/Common/Inputs/InputMap';
import PaneFormHeader, { PaneFormHeaderProps } from 'login-components/Common/PaneLayout/PaneFormHeader';
import PaneLayoutConstants from 'login-components/../constants/paneLayoutConstants';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import { getWindow } from 'login-utils/globalWrappers';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';

const styles = StyleSheet.create({
  container: combineStyles(PaneLayoutConstants.splitContentStaticWidthMixin, fonts.Roboto),
  formDialogue: {
    fontSize: 14,
    lineHeight: 1.4,
    letterSpacing: -0.015,
  },
  detailsWrapper: {
    color: colors.clayPlus3,
    fontSize: 11,
    paddingTop: 20,
  },
});

interface LoanEstimateFormSection {
  header: PaneFormHeaderProps;
  inputs: FormInput[];
}

const ESTIMATE_FORM_ERROR_ID = 'LoanEstimateForm-formError';
const loanEstimateFormSections: LoanEstimateFormSection[] = [
  {
    header: {
      text: 'Basic Information',
    },
    inputs: [
      {
        ...InputMap.SimpleTextInput,
        label: 'First name',
        placeholder: 'First name',
        identifier: 'first name',
        name: 'firstName',
      },
      {
        ...InputMap.SimpleTextInput,
        label: 'Last name',
        placeholder: 'Last name',
        identifier: 'last name',
        name: 'lastName',
      },
      {
        ...InputMap.EmailInput,
        label: 'Email address',
        placeholder: 'name@domain.com',
        identifier: 'email',
        name: 'emailAddress',
      },
      {
        ...InputMap.PhoneInput,
        label: 'Phone number',
        identifier: 'phone number',
        placeholder: '(123) 546-7890',
        name: 'phoneNumber',
        optional: true,
      },
    ],
  },
  {
    header: {
      text: 'Mailing Address',
    },
    inputs: [
      {
        ...InputMap.AddressInput,
        label: 'Mailing address',
        identifier: 'mailing address',
        name: 'mailingAddress',
        optional: true,
      },
    ],
  },
  {
    header: {
      text: 'Property Address',
    },
    inputs: [
      {
        ...InputMap.AddressInput,
        label: 'Property address',
        identifier: 'property address',
        name: 'subjectPropertyAddress',
      },
    ],
  },
  {
    header: {
      text: 'Loan Information',
    },
    inputs: [
      {
        ...InputMap.MoneyInput,
        label: 'Estimated property value',
        identifier: 'estimated property value',
        name: 'estimatedPropertyValue',
        placeholder: '100,000',
      },
      {
        ...InputMap.MoneyInput,
        label: 'Loan amount',
        identifier: 'loan amount',
        name: 'loanAmount',
        placeholder: '80,000',
      },
      {
        ...InputMap.SSNInput,
        label: 'Social security number',
        placeholder: '123-45-6789',
        identifier: 'social',
        name: 'SSN',
      },
      {
        ...InputMap.MoneyInput,
        label: 'Annual income',
        placeholder: '40,000',
        identifier: 'annual income',
        name: 'statedAnnualIncome',
      },
    ],
  },
];

const captchaInput: FormInput = {
  ...InputMap.Captcha,
  identifier: 'captcha',
  name: 'captchaResponse',
};

const actionText = 'Request a loan estimate';
const sectionInputs = _.flatMap(loanEstimateFormSections, 'inputs');
const inputsToValidate = sectionInputs.concat(captchaInput);

interface LoanEstimateFormProps extends FormProps<any, any, any> {
  onSubmit(values: LoanEstimate): void;
}

export class LoanEstimateForm extends React.Component<LoanEstimateFormProps, {}> {
  render() {
    const { handleSubmit, onSubmit, error } = this.props;
    const emailDisclaimerText = formUtils.getEmailDisclaimerText();
    return (
      <form className={css(paneFormStyles.wrapper)} noValidate onSubmit={handleSubmit!(onSubmit)}>
        {loanEstimateFormSections.map((definition, i) => (
          <div className={css(paneFormStyles.sectionWrapper)} key={i}>
            <PaneFormHeader {...definition.header} />
            <div className={css(paneFormStyles.inputsWrapper)}>
              {definition.inputs.map(input => (
                <div className={css(paneFormStyles.fieldWrapper)} key={input.name}>
                  {// We need to branch on this because AddressInputs' children are already Fields.
                  // If we want to do this again, we should abstract this into a "FormInput"
                  // component of sorts.
                  input.component === AddressInput ? <AddressInput name={input.name} /> : <Field {...input} />}
                </div>
              ))}
            </div>
          </div>
        ))}
        {/*
            We don't render the captcha input for E2E tests because captchas are engineered
            to be hard to click programmatically.
          */}

        {!getWindow().E2E_TEST && (
          <div className="LoanEstimateForm-captchaWrapper">
            <Field {...captchaInput} />
          </div>
        )}

        {error && (
          <div className={css(paneFormStyles.errorWrapper)}>
            <ErrorMessage errorText={error} errorId={ESTIMATE_FORM_ERROR_ID} />
          </div>
        )}
        {emailDisclaimerText && (
          <div className={css(styles.detailsWrapper)} data-it="email-disclaimer-text">
            {emailDisclaimerText}
          </div>
        )}
        <div
          className={css(styles.detailsWrapper)}
        >{`By clicking ${actionText}, you authorize us to obtain a copy of your credit report.`}</div>

        <div className={css(paneFormStyles.buttonDisplay, paneFormStyles.buttonPadding)}>
          <PrimaryButton isWide buttonId="it-LoanEstimateForm-submitButton" text={actionText} />
        </div>
      </form>
    );
  }
}

const ConnectedLoanEstimateForm = reduxForm<any, any>({
  form: 'loanEstimate',
  validate: /* istanbul ignore next: (UI-5482) */ values => formUtils.validate(inputsToValidate, values) as any,
  onSubmitFail: /* istanbul ignore next: (UI-5482) */ syncErrors =>
    formUtils.focusOnSubmitFail(syncErrors, ESTIMATE_FORM_ERROR_ID),
})(LoanEstimateForm);

export default ConnectedLoanEstimateForm;
